/**
 * v4.11.0 — Card Fleet Ops · primary orchestration page.
 *
 * Pure aggregation surface. Composes:
 *   - FleetKpiBar          (top strip — counts + fleet health)
 *   - FleetFilters         (org / location / status / search)
 *   - FleetTable           (paginated, with ScheduleSnapshot visual)
 *   - FleetDetailDrawer    (in-page side panel)
 *   - FleetOverviewPanels  (schedule mix · top locations · fleet health donut)
 *
 * Hospitality-first: dark surface, calm density, generous spacing, no
 * enterprise clutter. Trilingual via the new `card_fleet.*` namespace.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { locationsAPI, organizationsAPI } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Boxes } from "lucide-react";
import FleetActionRow from "../components/card_fleet/FleetActionRow";
import IssueCardsDialog from "../components/cards/IssueCardsDialog";
import BulkCreateCardsDialog from "../components/cards/BulkCreateCardsDialog";
import VoucherSettings from "./VoucherSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SetupGuidanceCard } from "../components/setup_guide/SetupGuidanceCard";
import { HelpGlyph } from "../components/setup_guide/HelpGlyph";
import FleetKpiBar from "../components/card_fleet/FleetKpiBar";
import FleetFilters from "../components/card_fleet/FleetFilters";
import FleetTable from "../components/card_fleet/FleetTable";
import FleetDetailDrawer from "../components/card_fleet/FleetDetailDrawer";
import FleetOverviewPanels from "../components/card_fleet/FleetOverviewPanels";
import SelectionKpiBar from "../components/card_fleet/bulk/SelectionKpiBar";
import BulkCommandStrip from "../components/card_fleet/bulk/BulkCommandStrip";
import BulkPreviewModal from "../components/card_fleet/bulk/BulkPreviewModal";
import {
    StatusConfigurator,
    ModeConfigurator,
    PlatformConfigurator,
    AssignmentConfigurator,
    ScheduleConfigurator,
} from "../components/card_fleet/bulk/BulkConfigurators";

const DEFAULT_FILTERS = {
    organization_id: "",
    location_id: "",
    status: "",
    mode: "",
    schedule: "",
    search: "",
    page: 1,
    page_size: 25,
};

const RETURN_STATE_KEY = "yot.fleet_ops.return_state";
const FILTER_STATE_KEY = "yot.fleet_ops.filters";

const loadStashedFilters = () => {
    try {
        const raw = sessionStorage.getItem(FILTER_STATE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
};

/**
 * `embedded` — rendered inside the Command Center (restaurant). Drops the page
 * heading (the Command Center owns it) and shows the card-focused action row.
 * `scopeLocationIds` — the Command Center scope. It narrows the whole fleet
 * server-side while the fleet's own location dropdown stays usable for
 * narrowing WITHIN that scope.
 * `scopeOrgId` — THE ORGANIZATION CHOSEN AT THE TOP OF THE COMMAND CENTER. It
 * is the authority: it overrides any filter left in sessionStorage, hides the
 * fleet's own organization dropdown (the page already has one, at the top), and
 * resets the table's page and selection whenever it changes. Empty string means
 * "All organizations", which behaves exactly as it always did.
 */
export default function CardFleetOps({ embedded = false, scopeOrgId = '', scopeLocationIds = [] }) {
    const { t } = useTranslation();
    const { user, hasPermission } = useAuth();
    const isSuper = user?.role === "super_admin";
    const scopeKey = (scopeLocationIds || []).join(",");

    const [filters, setFilters] = useState(() => {
        const base = loadStashedFilters() || DEFAULT_FILTERS;
        // A stashed org filter must never outvote the Command Center's choice.
        return scopeOrgId ? { ...base, organization_id: scopeOrgId, location_id: "", page: 1 } : base;
    });
    const [summary, setSummary] = useState(null);
    const [overview, setOverview] = useState(null);
    const [page, setPage] = useState({ items: [], total: 0, page: 1, pages: 1, page_size: 25 });
    const [loading, setLoading] = useState(true);
    const [drawerCardId, setDrawerCardId] = useState(null);

    // ── Command Center action row (embedded mode only) ──
    const [issueOpen, setIssueOpen] = useState(false);
    const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
    const [vouchersOpen, setVouchersOpen] = useState(false);
    const [pickerLocations, setPickerLocations] = useState([]);
    const [pickerOrgs, setPickerOrgs] = useState([]);
    const healthRef = useRef(null);

    // ── v4.12.0 — Phase 2 · Bulk Operations selection state ──
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [selectionSummary, setSelectionSummary] = useState(null);
    const [activeConfigurator, setActiveConfigurator] = useState(null); // "status" | "mode" | "platform" | "assignment" | "schedule"
    const [pendingOp, setPendingOp] = useState(null);                   // { op, payload }

    const toggleSelection = useCallback((id, on) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (on) next.add(id); else next.delete(id);
            return next;
        });
    }, []);
    const toggleAllOnPage = useCallback((on) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const r of page.items) {
                if (on) next.add(r.id); else next.delete(r.id);
            }
            return next;
        });
    }, [page.items]);
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    // Refresh the selection summary whenever selection size changes.
    useEffect(() => {
        if (selectedIds.size === 0) { setSelectionSummary(null); return; }
        let alive = true;
        api.post("/card-fleet/bulk/selection-summary",
            { card_ids: [...selectedIds], scope_organization_id: scopeOrgId || null })
            .then((r) => { if (alive) setSelectionSummary(r.data); })
            .catch(() => { if (alive) setSelectionSummary(null); });
        return () => { alive = false; };
    }, [selectedIds, scopeOrgId]);

    // v4.11.2 — Persist filters so a round-trip through /cards returns to
    // the exact same Fleet Ops state.
    useEffect(() => {
        try { sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
    }, [filters]);

    // v4.11.2 — On mount, if a return_state is stashed from a previous
    // navigation away to /cards, restore drawer + scroll position.
    useEffect(() => {
        let raw;
        try { raw = sessionStorage.getItem(RETURN_STATE_KEY); } catch { raw = null; }
        if (!raw) return;
        let stashed;
        try { stashed = JSON.parse(raw); } catch { stashed = null; }
        // One-shot: consume even if malformed.
        try { sessionStorage.removeItem(RETURN_STATE_KEY); } catch { /* ignore */ }
        if (!stashed) return;
        // Stale safeguard — anything older than 10 minutes is ignored.
        if (stashed.stamped_at && Date.now() - stashed.stamped_at > 10 * 60 * 1000) return;
        if (stashed.drawer_card_id) setDrawerCardId(stashed.drawer_card_id);
        if (typeof stashed.scroll_y === "number") {
            // Run after first paint so the rows have laid out.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.scrollTo({ top: stashed.scroll_y, behavior: "instant" }));
            });
        }
    }, []);

    // THE TOP ORGANIZATION CHANGED — re-point the fleet at it, drop any
    // narrower location filter, go back to page 1 and clear the selection, so
    // a selection made in one store can never be applied in another.
    useEffect(() => {
        if (!embedded) return;
        setFilters((f) => (f.organization_id === (scopeOrgId || "") && f.page === 1
            ? f
            : { ...f, organization_id: scopeOrgId || "", location_id: "", page: 1 }));
        setSelectedIds(new Set());
    }, [embedded, scopeOrgId]);

    // Build axios params from current filters (drop empty strings).
    const params = useMemo(() => {
        const out = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v !== "" && v !== null && v !== undefined) out[k] = v;
        }
        // The Command Center's organization WINS over anything in the fleet's
        // own state, including a value restored from sessionStorage.
        if (scopeOrgId) out.organization_id = scopeOrgId;
        // Command Center scope — server-side, so KPIs, pagination and the
        // Tag ID order all respect it. The fleet's own location filter (when
        // set) is narrower and takes precedence server-side.
        if (scopeKey) out.location_ids = scopeKey;
        return out;
    }, [filters, scopeKey, scopeOrgId]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, c, o] = await Promise.all([
                api.get("/card-fleet/summary", { params }),
                api.get("/card-fleet/cards", { params }),
                api.get("/card-fleet/overview", { params }),
            ]);
            setSummary(s.data);
            setPage(c.data);
            setOverview(o.data);
        } catch (err) {
            // Fail silently — empty data simply renders an empty state.
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const updateFilter = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 }));
    const updatePage = (nextPage) => setFilters((f) => ({ ...f, page: nextPage }));

    // Location / organization lists for the two create dialogs (embedded only).
    useEffect(() => {
        if (!embedded) return;
        locationsAPI.list().then((r) => setPickerLocations(r.data || [])).catch(() => setPickerLocations([]));
        if (isSuper) organizationsAPI.list().then((r) => setPickerOrgs(r.data || [])).catch(() => setPickerOrgs([]));
    }, [embedded, isSuper]);

    // Every create dialog must offer ONLY the scoped organization's locations.
    const scopedPickerLocations = useMemo(() => (
        scopeOrgId
            ? (pickerLocations || []).filter((l) => l.organization_id === scopeOrgId)
            : (pickerLocations || [])
    ), [pickerLocations, scopeOrgId]);

    // Every action below opens an EXISTING fleet surface in place. The bulk
    // surfaces need a selection, so they say so instead of opening empty.
    const needSelection = useCallback((next) => {
        if (selectedIds.size === 0) { toast.info(t("card_fleet.ops_actions.select_cards_first")); return; }
        next();
    }, [selectedIds, t]);

    const openCardSettings = () => {
        if (selectedIds.size !== 1) { toast.info(t("card_fleet.ops_actions.select_one_card_first")); return; }
        setDrawerCardId([...selectedIds][0]);
    };

    const actionRow = embedded ? (
        <FleetActionRow
            onIssueNew={() => setIssueOpen(true)}
            onBulkCreate={() => setBulkCreateOpen(true)}
            onRefresh={() => { loadAll(); toast.success(t("card_fleet.ops_actions.refreshed")); }}
            onVouchers={() => setVouchersOpen(true)}
            showVouchers={!!hasPermission?.('manage_vouchers')}
            onCardSettings={openCardSettings}
            onSchedules={() => needSelection(() => setActiveConfigurator("schedule"))}
            onAssign={() => needSelection(() => setActiveConfigurator("assignment"))}
            onDestination={() => needSelection(() => setActiveConfigurator("platform"))}
            onMode={() => needSelection(() => setActiveConfigurator("mode"))}
            onBulkActions={() => needSelection(() => setActiveConfigurator("status"))}
            onFleetHealth={() => healthRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
        />
    ) : null;

    return (
        <div className="space-y-6" data-testid="card-fleet-ops-page">
            {!embedded && (
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1
                        className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                        <Boxes className="w-7 h-7 text-sky-400" strokeWidth={1.75} />
                        {t("card_fleet.page_title")}
                        <HelpGlyph guideKey="card_fleet" testId="card-fleet-help-glyph" />
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {t("card_fleet.page_subtitle")}
                    </p>
                </div>
            </div>
            )}

            {actionRow}

            <SetupGuidanceCard
                guideKey="card_fleet"
                points={[
                    "setup_guide.card_fleet.point_overview",
                    "setup_guide.card_fleet.point_schedule",
                    "setup_guide.card_fleet.point_filter",
                ]}
                testId="setup-guide-card-fleet"
            />

            {selectedIds.size > 0 ? (
                <SelectionKpiBar
                    count={selectedIds.size}
                    summary={selectionSummary}
                    onClearSelection={clearSelection}
                />
            ) : (
                <FleetKpiBar summary={summary} loading={loading} />
            )}

            <FleetFilters
                filters={filters}
                onChange={updateFilter}
                showOrgFilter={isSuper && !scopeOrgId}
                scopeOrgId={scopeOrgId}
            />

            <FleetTable
                rows={page.items}
                total={page.total}
                page={page.page}
                pages={page.pages}
                pageSize={page.page_size}
                onPageChange={updatePage}
                onPageSizeChange={(ps) => setFilters((f) => ({ ...f, page_size: ps, page: 1 }))}
                onRowClick={(c) => setDrawerCardId(c.id)}
                loading={loading}
                selectedIds={selectedIds}
                onToggleSelection={toggleSelection}
                onToggleAllOnPage={toggleAllOnPage}
            />

            <div ref={healthRef}>
                <FleetOverviewPanels overview={overview} loading={loading} />
            </div>

            <FleetDetailDrawer
                cardId={drawerCardId}
                onClose={() => setDrawerCardId(null)}
            />

            <BulkCommandStrip
                count={selectedIds.size}
                onClear={clearSelection}
                onOpenSchedule={() => setActiveConfigurator("schedule")}
                onOpenRouting={() => setActiveConfigurator("platform")}
                onOpenMode={() => setActiveConfigurator("mode")}
                onOpenStatus={() => setActiveConfigurator("status")}
                onOpenAssignment={() => setActiveConfigurator("assignment")}
            />

            <StatusConfigurator
                open={activeConfigurator === "status"}
                onClose={() => setActiveConfigurator(null)}
                onChoose={(p) => { setActiveConfigurator(null); setPendingOp({ op: "status", payload: p }); }}
            />
            <ModeConfigurator
                open={activeConfigurator === "mode"}
                onClose={() => setActiveConfigurator(null)}
                onChoose={(p) => { setActiveConfigurator(null); setPendingOp({ op: "mode", payload: p }); }}
            />
            <PlatformConfigurator
                open={activeConfigurator === "platform"}
                onClose={() => setActiveConfigurator(null)}
                onChoose={(p) => { setActiveConfigurator(null); setPendingOp({ op: "platform", payload: p }); }}
            />
            <AssignmentConfigurator
                open={activeConfigurator === "assignment"}
                onClose={() => setActiveConfigurator(null)}
                onChoose={(p) => { setActiveConfigurator(null); setPendingOp({ op: "assignment", payload: p }); }}
                locationFilter={filters.location_id || ""}
            />
            <ScheduleConfigurator
                open={activeConfigurator === "schedule"}
                onClose={() => setActiveConfigurator(null)}
                onChoose={(p) => { setActiveConfigurator(null); setPendingOp({ op: "schedule", payload: p }); }}
                selectedCardIds={[...selectedIds]}
            />

            <BulkPreviewModal
                open={!!pendingOp}
                op={pendingOp?.op}
                payload={pendingOp?.payload || {}}
                cardIds={[...selectedIds]}
                scopeOrgId={scopeOrgId}
                onClose={() => setPendingOp(null)}
                onApplied={() => {
                    setPendingOp(null);
                    clearSelection();
                    loadAll();
                }}
            />

            {embedded && (
                <>
                    <IssueCardsDialog
                        open={issueOpen}
                        onOpenChange={setIssueOpen}
                        locations={scopedPickerLocations}
                        organizations={pickerOrgs}
                        isSuperAdmin={isSuper}
                        defaultOrganizationId={scopeOrgId || ''}
                        defaultLocationId={filters.location_id || (scopedPickerLocations.length === 1 ? scopedPickerLocations[0].id : '') || (scopeLocationIds?.length === 1 ? scopeLocationIds[0] : '')}
                        onCreated={loadAll}
                    />
                    <BulkCreateCardsDialog
                        open={bulkCreateOpen}
                        onOpenChange={setBulkCreateOpen}
                        organizationId={scopeOrgId || (isSuper ? (filters.organization_id || '') : (user?.organization_id || ''))}
                        organizations={pickerOrgs}
                        locations={scopedPickerLocations}
                        defaultLocationId={filters.location_id || (scopedPickerLocations.length === 1 ? scopedPickerLocations[0].id : '') || (scopeLocationIds?.length === 1 ? scopeLocationIds[0] : '')}
                        onCreated={() => loadAll()}
                    />
                    {/* Vouchers — the real Vouchers surface, embedded (no second
                        page heading) so the operator never leaves the Command Center. */}
                    <Dialog open={vouchersOpen} onOpenChange={setVouchersOpen}>
                        <DialogContent className="max-w-[min(1200px,96vw)] max-h-[92vh] overflow-y-auto" data-testid="cc-fleet-vouchers-dialog">
                            <DialogHeader>
                                <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
                                    {t('card_fleet.ops_actions.vouchers')}
                                </DialogTitle>
                            </DialogHeader>
                            {vouchersOpen && <VoucherSettings embedded scopeOrgId={scopeOrgId} />}
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </div>
    );
}
